import { useCallback } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import SaveIcon from '@/material-icons/400-24px/save.svg?react';
import { saveComposeDraft } from 'mastodon/actions/drafts';
import { openModal } from 'mastodon/actions/modal';
import { IconButton } from 'mastodon/components/icon_button';
import { useAppDispatch, useAppSelector } from 'mastodon/store';

const messages = defineMessages({
  save: { id: 'compose_form.save_draft', defaultMessage: 'Save as draft' },
  update: {
    id: 'compose_form.update_draft',
    defaultMessage: 'Update the draft you are continuing',
  },
  attachmentsTitle: {
    id: 'compose_form.draft_attachments.title',
    defaultMessage: 'Save without attachments?',
  },
  attachmentsMessage: {
    id: 'compose_form.draft_attachments.message',
    defaultMessage:
      'Drafts keep the text only. Attached media and polls are not saved, and the form is cleared after saving.',
  },
  attachmentsConfirm: {
    id: 'compose_form.draft_attachments.confirm',
    defaultMessage: 'Save text only',
  },
});

/** Saves the post being written as a draft (Drafts in the navigation) */
export const DraftButton: React.FC = () => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const isEditing = useAppSelector((state) => state.compose.get('id') !== null);
  const isDraft = useAppSelector((state) => !!state.compose.get('draft_id'));
  // Drafts only keep the text, and saving clears the form
  const hasAttachments = useAppSelector(
    (state) =>
      (state.compose.get('media_attachments') as { size: number }).size > 0 ||
      !!state.compose.get('poll'),
  );

  const handleClick = useCallback(() => {
    const save = () => void dispatch(saveComposeDraft());

    if (hasAttachments) {
      dispatch(
        openModal({
          modalType: 'CONFIRM',
          modalProps: {
            title: intl.formatMessage(messages.attachmentsTitle),
            message: intl.formatMessage(messages.attachmentsMessage),
            confirm: intl.formatMessage(messages.attachmentsConfirm),
            onConfirm: save,
          },
        }),
      );
    } else {
      save();
    }
  }, [dispatch, hasAttachments, intl]);

  if (isEditing) return null;

  return (
    <IconButton
      icon='save'
      iconComponent={SaveIcon}
      title={intl.formatMessage(isDraft ? messages.update : messages.save)}
      active={isDraft}
      onClick={handleClick}
      inverted
    />
  );
};
